import { useAuth } from "@/contexts/AuthContext";
import React, { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface Task {
  id: number;
  title: string;
  date?: string;
  pinned?: boolean;
  jobId?: string;
  /** 课程文件夹内展示顺序（与 jobs_to_copy.txt 一致，越小越靠前） */
  sidebarFolderOrder?: number;
}

export interface Course {
  id: string;
  name: string;
  description?: string;
  tasks: Task[];
}

interface CourseContextType {
  selectedCourseId: string | null;
  setSelectedCourseId: (id: string | null) => void;
  courses: Course[];
  setCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  selectedCourse: Course | null;
  selectedTaskId: string | null;
  setSelectedTaskId: (id: string | null) => void;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

function createCourseStorageKey(userId: string) {
  return `teachmaster_courses:${userId}`;
}

function readCoursesFromStorage(userId: string) {
  try {
    const raw = localStorage.getItem(createCourseStorageKey(userId));
    if (!raw) {
      return [] as Course[];
    }
    const parsed = JSON.parse(raw) as Course[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [] as Course[];
  }
}

export function CourseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id || "";
  const storageKey = useMemo(() => (userId ? createCourseStorageKey(userId) : ""), [userId]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [storageReady, setStorageReady] = useState(false);

  useEffect(() => {
    setStorageReady(false);

    if (!userId) {
      setCourses([]);
      setSelectedCourseId(null);
      setSelectedTaskId(null);
      setStorageReady(true);
      return;
    }

    setCourses(readCoursesFromStorage(userId));
    setSelectedCourseId(null);
    setSelectedTaskId(null);
    setStorageReady(true);
  }, [userId]);

  useEffect(() => {
    if (!storageKey || !storageReady) {
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(courses));
  }, [courses, storageKey, storageReady]);

  const selectedCourse = courses.find(course => course.id === selectedCourseId) || null;

  return (
    <CourseContext.Provider
      value={{
        selectedCourseId,
        setSelectedCourseId,
        courses,
        setCourses,
        selectedCourse,
        selectedTaskId,
        setSelectedTaskId,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
}

export function useCourse() {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error("useCourse must be used within a CourseProvider");
  }
  return context;
}
